import React, { useMemo, useState } from 'react';
import styles from './BookletSection.module.css';

/** Served from public/booklets/iGiTREE-Booklet.pdf */
const PDF_PATH = '/booklets/iGiTREE-Booklet.pdf';
const PDF_FILENAME = 'iGiTREE - Booklet.pdf';
const GOOGLE_DRIVE_PDF_URL =
  'https://drive.google.com/file/d/18Bc_HDfv594w1cAeg7AoN4egGCYC79G8/view?usp=sharing';
/** Deep link for QR codes → Welcome page BookletSection */
const BOOKLET_SECTION_HASH = '/#booklet';

const PREVIEW_PAGES = [
  {
    page: 1,
    label: 'Cover',
    src: '/booklets/previews/page-1.jpg',
  },
  {
    page: 2,
    label: 'Inside page',
    src: '/booklets/previews/page-2.jpg',
  },
  {
    page: 3,
    label: 'Inside page',
    src: '/booklets/previews/page-3.jpg',
  },
];

const BookletSection: React.FC = () => {
  const [activePage, setActivePage] = useState(PREVIEW_PAGES[0]);

  const bookletSectionUrl = useMemo(() => {
    if (typeof window === 'undefined') return BOOKLET_SECTION_HASH;
    return `${window.location.origin}${BOOKLET_SECTION_HASH}`;
  }, []);

  const qrImageUrl = useMemo(
    () =>
      `https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(bookletSectionUrl)}`,
    [bookletSectionUrl]
  );

  return (
    <section id="booklet" className={styles.section} aria-labelledby="booklet-title">
      <div className={styles.inner}>
        <header className={styles.header}>
          <p className={styles.kicker}>Free download</p>
          <h2 id="booklet-title" className={styles.title}>
            iGiTREE Booklet
          </h2>
          <p className={styles.subtitle}>
            Preview pages from the booklet, scan the QR code, or download the full PDF to keep offline.
          </p>
        </header>

        <div className={styles.layout}>
          <div className={styles.previewPane}>
            <div className={styles.previewStage}>
              <img
                src={activePage.src}
                alt={`Preview of ${PDF_FILENAME}, page ${activePage.page}`}
                className={styles.previewImage}
                loading="lazy"
                decoding="async"
              />
            </div>

            <div className={styles.thumbnailRow} aria-label="Booklet preview pages">
              {PREVIEW_PAGES.map((preview) => (
                <button
                  key={preview.page}
                  type="button"
                  className={`${styles.thumbnailButton} ${
                    activePage.page === preview.page ? styles.thumbnailButtonActive : ''
                  }`}
                  onClick={() => setActivePage(preview)}
                  aria-label={`Show preview page ${preview.page}`}
                >
                  <img
                    src={preview.src}
                    alt=""
                    className={styles.thumbnailImage}
                    aria-hidden="true"
                    loading="lazy"
                    decoding="async"
                  />
                  <span>
                    {preview.label} {preview.page}
                  </span>
                </button>
              ))}
            </div>

            <div className={styles.previewActions}>
              <a className={styles.btnPrimary} href={PDF_PATH} download={PDF_FILENAME}>
                Download PDF
              </a>
              <a
                className={styles.btnSecondary}
                href={GOOGLE_DRIVE_PDF_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open in new tab
              </a>
            </div>
          </div>

          <aside className={styles.qrPane}>
            <div className={styles.qrCard}>
              <h3 className={styles.qrTitle}>Scan to open this section</h3>
              <img
                src={qrImageUrl}
                alt="QR code linking to the iGiTREE Booklet section"
                className={styles.qrImage}
                width={250}
                height={250}
                loading="lazy"
                decoding="async"
              />
              <a className={styles.link} href={PDF_PATH} download={PDF_FILENAME}>
                Or click here to download the PDF
              </a>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
};

export default BookletSection;
